import React, { useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { useWebSocket } from "../../hooks/useWebSocket";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Card, CardContent } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { 
  CreditCard, Phone, CheckCircle, Clock, Receipt, Printer,
  Search, DollarSign, LogOut, RefreshCw, User, Bell, RotateCcw, Plus,
  Edit, Minus, X, Split, ChefHat
} from "lucide-react";
import { cn, formatCurrency, formatDate, formatTime, ORDER_STATUSES, PAYMENT_STATUSES } from "../../lib/utils";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CashierDashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [paymentDialog, setPaymentDialog] = useState(false);
  const [receiptDialog, setReceiptDialog] = useState(false);
  const [paperSize, setPaperSize] = useState(() => localStorage.getItem("dalabsom_paper_size") || "80");
  const [autoPrint, setAutoPrint] = useState(() => localStorage.getItem("dalabsom_auto_print") === "1");
  const [editDialog, setEditDialog] = useState(false);
  const [splitDialog, setSplitDialog] = useState(false);
  const [changeMethodDialog, setChangeMethodDialog] = useState(false);
  const [transactionCode, setTransactionCode] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [processing, setProcessing] = useState(false);
  const [filter, setFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [restaurant, setRestaurant] = useState(null);
  const [refundDialog, setRefundDialog] = useState(false);
  const [refundReason, setRefundReason] = useState("");
  const [menuItems, setMenuItems] = useState([]);
  const [menuCategories, setMenuCategories] = useState([]);
  const [editItems, setEditItems] = useState([]);
  const [newPaymentMethod, setNewPaymentMethod] = useState("");
  const [splitAmount, setSplitAmount] = useState("");
  const [splitMethod, setSplitMethod] = useState("cash");
  const [splitPayerName, setSplitPayerName] = useState("");
  const [assistanceRequests, setAssistanceRequests] = useState([]);
  const receiptRef = useRef(null);
  // Indirection via ref so handleWebSocketMessage (defined earlier) can call
  // printOrderReceipt without triggering a TDZ ReferenceError.
  const printOrderReceiptRef = useRef(() => {});

  const playNotificationSound = useCallback(() => {
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain); gain.connect(ctx.destination);
      osc.frequency.value = 880; osc.type = "sine";
      gain.gain.setValueAtTime(0.2, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.3);
      osc.start(); osc.stop(ctx.currentTime + 0.3);
    } catch (e) {}
  }, []);

  const playAssistanceSound = useCallback(() => {
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const play = (f, t, d) => {
        const o = ctx.createOscillator(); const g = ctx.createGain();
        o.connect(g); g.connect(ctx.destination);
        o.frequency.value = f; o.type = "sine";
        g.gain.setValueAtTime(0.3, t);
        g.gain.exponentialRampToValueAtTime(0.01, t + d);
        o.start(t); o.stop(t + d);
      };
      const n = ctx.currentTime;
      play(880, n, 0.15); play(1100, n + 0.18, 0.15); play(1320, n + 0.36, 0.3);
    } catch (e) {}
  }, []);

  const handleWebSocketMessage = useCallback((message) => {
    if (message.type === "call_waiter" && message.request) {
      if (navigator.vibrate) navigator.vibrate([200, 100, 200]);
      playAssistanceSound();
      const r = message.request;
      toast.warning(`Customer at T${r.table_number || r.qr_name || "?"} needs assistance`, { duration: 8000 });
      setAssistanceRequests(prev => [r, ...prev.filter(x => x.id !== r.id)]);
      return;
    }
    if (message.type === "call_waiter_update" && message.request) {
      setAssistanceRequests(prev => {
        if (message.request.status === "resolved") return prev.filter(x => x.id !== message.request.id);
        return prev.map(x => x.id === message.request.id ? message.request : x);
      });
      return;
    }
    if (message.type === "payment_proof_submitted" && message.order) {
      if (navigator.vibrate) navigator.vibrate([100, 50, 100]);
      playNotificationSound();
      toast.info(`Payment proof received for #${message.order.order_number.split("-")[1]}`, { duration: 6000 });
      setOrders(prev => prev.map(o => (o.id === message.order.id ? message.order : o)));
      if (selectedOrder?.id === message.order.id) setSelectedOrder(message.order);
      return;
    }
    if (message.type === "new_order") {
      setOrders(prev => [message.order, ...prev.filter(o => o.id !== message.order.id)]);
      playNotificationSound();
      toast.success(`New Order: #${message.order.order_number.split("-")[1]}`, { duration: 5000 });
      // Auto-print on new order so the waiter can immediately bring receipt to table.
      if (autoPrint) {
        setTimeout(() => printOrderReceiptRef.current(message.order), 300);
      }
    } else if (["order_status_update", "payment_confirmed", "payment_verified", "order_updated", "split_payment_processed", "split_payment_added", "order_refunded"].includes(message.type)) {
      setOrders(prev => prev.map(o => (o.id === message.order.id ? message.order : o)));
      if (selectedOrder?.id === message.order.id) setSelectedOrder(message.order);
    }
  }, [selectedOrder, playNotificationSound, playAssistanceSound, autoPrint]);

  useWebSocket(user?.restaurant_id, handleWebSocketMessage);

  useEffect(() => {
    fetchOrders();
    fetchRestaurant();
    fetchMenu();
    fetchAssistance();
  }, []);

  const fetchAssistance = async () => {
    try {
      const r = await axios.get(`${API}/restaurant/assistance-requests`);
      setAssistanceRequests(r.data || []);
    } catch (e) { /* silent */ }
  };

  const resolveAssistance = async (reqId) => {
    try {
      await axios.put(`${API}/restaurant/assistance-requests/${reqId}?status=resolved`);
      setAssistanceRequests(prev => prev.filter(r => r.id !== reqId));
      toast.success("Assistance marked resolved");
    } catch (e) { toast.error("Failed"); }
  };

  const fetchRestaurant = async () => {
    try {
      const r = await axios.get(`${API}/restaurant/profile`);
      setRestaurant(r.data);
    } catch (e) { console.error("Failed to fetch restaurant:", e); }
  };

  const fetchMenu = async () => {
    try {
      const [catRes, itemRes] = await Promise.all([
        axios.get(`${API}/restaurant/categories`),
        axios.get(`${API}/restaurant/items`),
      ]);
      setMenuCategories(catRes.data);
      setMenuItems(itemRes.data);
    } catch (e) { console.error("Failed to fetch menu:", e); }
  };

  const fetchOrders = async () => {
    try {
      const r = await axios.get(`${API}/orders`);
      setOrders(r.data);
    } catch (e) { console.error("Failed to fetch orders:", e); }
    finally { setLoading(false); }
  };

  const confirmPayment = async () => {
    setProcessing(true);
    try {
      const r = await axios.post(`${API}/orders/${selectedOrder.id}/confirm-payment`);
      setOrders(prev => prev.map(o => (o.id === r.data.id ? r.data : o)));
      setSelectedOrder(r.data);
      toast.success("Payment confirmed!");
      setPaymentDialog(false);
    } catch (e) { toast.error("Failed to confirm payment"); }
    finally { setProcessing(false); }
  };

  const markPaidAtCounter = async () => {
    setProcessing(true);
    try {
      const r = await axios.post(`${API}/orders/${selectedOrder.id}/mark-paid-at-counter`);
      setOrders(prev => prev.map(o => (o.id === r.data.id ? r.data : o)));
      setSelectedOrder(r.data);
      toast.success("Marked as paid!");
      setPaymentDialog(false);
    } catch (e) { toast.error("Failed to update payment"); }
    finally { setProcessing(false); }
  };

  // Somalia hybrid payment verification — confirm or reject SMS-matched payment
  const verifySomaliaPayment = async (status, notes = "") => {
    setProcessing(true);
    try {
      const r = await axios.put(`${API}/orders/${selectedOrder.id}/payment/verify`, { status, notes });
      setOrders(prev => prev.map(o => (o.id === r.data.id ? r.data : o)));
      setSelectedOrder(r.data);
      toast.success(status === "confirmed" ? "Payment confirmed ✓" : "Payment rejected");
    } catch (e) { toast.error(e.response?.data?.detail || "Verification failed"); }
    finally { setProcessing(false); }
  };

  const processRefund = async () => {
    if (!refundReason.trim()) { toast.error("Enter refund reason"); return; }
    setProcessing(true);
    try {
      const r = await axios.post(`${API}/orders/${selectedOrder.id}/refund`, { reason: refundReason });
      setOrders(prev => prev.map(o => (o.id === r.data.id ? r.data : o)));
      setSelectedOrder(r.data);
      toast.success("Refund processed!");
      setRefundDialog(false); setRefundReason("");
    } catch (e) { toast.error(e.response?.data?.detail || "Refund failed"); }
    finally { setProcessing(false); }
  };

  const changePaymentMethod = async () => {
    if (!newPaymentMethod) return;
    setProcessing(true);
    try {
      const r = await axios.put(`${API}/orders/${selectedOrder.id}/payment-method`, { payment_method: newPaymentMethod });
      setOrders(prev => prev.map(o => (o.id === r.data.id ? r.data : o)));
      setSelectedOrder(r.data);
      toast.success("Payment method updated!");
      setChangeMethodDialog(false);
    } catch (e) { toast.error("Failed to change payment method"); }
    finally { setProcessing(false); }
  };

  const updateOrderItems = async () => {
    if (editItems.length === 0) { toast.error("Order must have at least one item"); return; }
    setProcessing(true);
    try {
      const r = await axios.put(`${API}/orders/${selectedOrder.id}/items`, {
        items: editItems.map(i => ({
          menu_item_id: i.menu_item_id,
          quantity: i.quantity,
          variant_id: i.variant_id || null,
          selected_extras: i.selected_extras || [],
          notes: i.notes || null
        }))
      });
      setOrders(prev => prev.map(o => (o.id === r.data.id ? r.data : o)));
      setSelectedOrder(r.data);
      toast.success("Order updated!");
      setEditDialog(false);
    } catch (e) { toast.error("Failed to update order"); }
    finally { setProcessing(false); }
  };

  const addSplitPayment = async () => {
    const amount = parseFloat(splitAmount);
    if (!amount || amount <= 0) { toast.error("Enter valid amount"); return; }
    setProcessing(true);
    try {
      const r = await axios.post(`${API}/orders/${selectedOrder.id}/add-split`, {
        amount, payment_method: splitMethod, payer_name: splitPayerName || null
      });
      setOrders(prev => prev.map(o => (o.id === r.data.id ? r.data : o)));
      setSelectedOrder(r.data);
      toast.success("Split payment added!");
      setSplitAmount(""); setSplitPayerName("");
    } catch (e) { toast.error("Failed to add split"); }
    finally { setProcessing(false); }
  };

  const processSplitPayment = async (splitId) => {
    setProcessing(true);
    try {
      const r = await axios.post(`${API}/orders/${selectedOrder.id}/split-payment`, { split_payment_id: splitId });
      setOrders(prev => prev.map(o => (o.id === r.data.id ? r.data : o)));
      setSelectedOrder(r.data);
      toast.success("Split payment confirmed!");
    } catch (e) { toast.error("Failed to process split"); }
    finally { setProcessing(false); }
  };

  // Build a print-ready receipt HTML directly from an order object so it can be
  // triggered for ANY order (not just the one open in the dialog). Used by both
  // manual reprint and auto-print on new orders.
  const printOrderReceipt = useCallback((order, restaurantData) => {
    if (!order) return;
    try {
      const r = restaurantData || restaurant || {};
      const providers = (r.manual_providers || []).filter(p => p.active !== false);
      const isNarrow = paperSize === "58";
      const escape = (s) => String(s ?? "").replace(/[&<>]/g, (c) => ({"&":"&amp;","<":"&lt;",">":"&gt;"}[c]));
      const fmt = (n) => formatCurrency(n);
      const ts = new Date(order.created_at || Date.now()).toLocaleString("en", { dateStyle: "short", timeStyle: "short" });

      const itemRows = (order.items || []).map(it => {
        const extras = (it.extras_details || it.extras || []).map(e => `<div class="x">+ ${escape(e.name)}${e.price ? ` (${fmt(e.price)})` : ""}</div>`).join("");
        const variant = it.variant_name ? `<div class="x">[${escape(it.variant_name)}]</div>` : "";
        const notes = it.notes ? `<div class="n">Note: ${escape(it.notes)}</div>` : "";
        return `<div class="row"><div class="ln"><span>${it.quantity}× ${escape(it.name)}</span><span>${fmt(it.total)}</span></div>${variant}${extras}${notes}</div>`;
      }).join("");

      const providerRows = providers.map(p => {
        let ussd = p.merchant_number;
        if (p.ussd_code && p.ussd_code.includes("*")) {
          ussd = p.ussd_code
            .replace(/\{merchant\}/gi, p.merchant_number)
            .replace(/\{amount\}/gi, "$")
            .replace(/\{total\}/gi, "$");
        }
        return `<div class="pay"><div class="pn"><b>${escape(p.name)}</b><span>${escape(p.merchant_number)}</span></div><div class="ussd">${escape(ussd)}</div>${p.instructions ? `<div class="instr">${escape(p.instructions)}</div>` : ""}</div>`;
      }).join("");

      const tableLabel = order.order_type === "eat_in" ? `Table ${escape(order.table_number || "—")}` : "Takeaway";
      const isUnpaid = order.payment_status !== "paid";
      const totalLine = order.payment_unique_amount || order.total;

      const html = `<!doctype html><html><head><title>Receipt #${escape(order.order_number)}</title>
        <style>
          *{margin:0;padding:0;box-sizing:border-box}
          body{font-family:'Courier New',ui-monospace,monospace;font-size:${isNarrow ? 10 : 12}px;line-height:1.4;padding:${isNarrow ? 4 : 8}px;width:${isNarrow ? "58mm" : "80mm"};max-width:${isNarrow ? "58mm" : "80mm"};color:#000}
          .ctr{text-align:center}
          h1{font-size:${isNarrow ? 14 : 16}px;font-weight:900}
          .meta{font-size:11px;margin:6px 0;border-top:1px dashed #000;border-bottom:1px dashed #000;padding:4px 0}
          .meta .r{display:flex;justify-content:space-between}
          .row{padding:3px 0;border-bottom:1px dashed #ccc}
          .row:last-child{border-bottom:none}
          .ln{display:flex;justify-content:space-between;font-weight:bold}
          .x{font-size:11px;padding-left:8px;color:#444}
          .n{font-size:11px;padding-left:8px;background:#fff3cd;border-left:3px solid #000;margin-top:2px;padding:2px 4px}
          .tot{font-size:${isNarrow ? 13 : 15}px;font-weight:900;display:flex;justify-content:space-between;border-top:2px dashed #000;margin-top:6px;padding-top:6px}
          .status{margin-top:6px;text-align:center;font-weight:bold;padding:5px;border:2px solid #000;font-size:13px}
          .status.paid{background:#000;color:#fff}
          .status.unpaid{background:#fff3cd}
          .pmtbox{margin-top:10px;border-top:3px dashed #000;padding-top:8px}
          .pmtbox h2{margin-bottom:6px;text-align:center;font-size:${isNarrow ? 16 : 20}px;font-weight:900;letter-spacing:1px}
          .pay{padding:6px 0;border-bottom:1px dashed #ccc}
          .pay:last-child{border-bottom:none}
          .pn{display:flex;justify-content:space-between;font-weight:bold;font-size:${isNarrow ? 13 : 15}px}
          .ussd{font-family:'Courier New',monospace;font-size:${isNarrow ? 15 : 18}px;font-weight:900;text-align:center;padding:5px 3px;background:#000;color:#fff;margin-top:3px;letter-spacing:1px}
          .instr{font-size:11px;font-style:italic;margin-top:2px;color:#555}
          .ft{text-align:center;font-size:10px;margin-top:10px;color:#666}
          @page{size:${isNarrow ? "58mm" : "80mm"} auto;margin:0}
        </style></head><body>
        <div class="ctr">
          <h1>${escape(r.name || "Receipt")}</h1>
          ${r.address ? `<div style="font-size:11px">${escape(r.address)}</div>` : ""}
          ${r.contact_phone ? `<div style="font-size:11px">Tel: ${escape(r.contact_phone)}</div>` : ""}
        </div>
        <div class="meta">
          <div class="r"><span>Order</span><b>#${escape(order.order_number)}</b></div>
          <div class="r"><span>Type</span><b>${tableLabel}</b></div>
          <div class="r"><span>Date</span><b>${escape(ts)}</b></div>
          <div class="r"><span>Method</span><b>${escape((order.payment_method || "").toUpperCase().replace(/_/g, " "))}</b></div>
          ${order.customer_phone ? `<div class="r"><span>Customer</span><b>${escape(order.customer_phone)}</b></div>` : ""}
        </div>
        ${itemRows}
        <div class="tot"><span>TOTAL</span><span>${fmt(totalLine)}</span></div>
        <div class="status ${isUnpaid ? "unpaid" : "paid"}">${isUnpaid ? "PAYMENT PENDING" : "PAID"}</div>
        ${providers.length > 0 ? `<div class="pmtbox"><h2>PAYMENT OPTIONS</h2>${providerRows}<div style="font-size:11px;text-align:center;margin-top:6px"><b>Total to pay: ${fmt(totalLine)}</b><br/>Replace <b>$</b> with the amount when dialing.</div></div>` : ""}
        <div class="ft">— Thank you · Powered by Dalabsom —</div>
      </body></html>`;

      // Remove existing iframe
      const existing = document.getElementById("dalabsom-print-iframe");
      if (existing) existing.remove();
      const iframe = document.createElement("iframe");
      iframe.id = "dalabsom-print-iframe";
      iframe.style.cssText = "position:fixed;right:0;bottom:0;width:0;height:0;border:0;opacity:0;";
      document.body.appendChild(iframe);
      const doc = iframe.contentWindow.document;
      doc.open(); doc.write(html); doc.close();
      const doPrint = () => {
        try { iframe.contentWindow.focus(); iframe.contentWindow.print(); }
        catch (e) {
          console.error("iframe print failed, falling back to new window:", e);
          // Fallback: open the receipt in a new tab so user can print/save.
          const w = window.open("", "_blank");
          if (w) { w.document.open(); w.document.write(html); w.document.close(); }
          else { toast.error("Allow popups to print receipts"); }
        }
        setTimeout(() => { try { iframe.remove(); } catch (_) {} }, 60000);
      };
      if (iframe.contentDocument.readyState === "complete") setTimeout(doPrint, 200);
      else iframe.onload = () => setTimeout(doPrint, 200);
    } catch (e) {
      console.error("printOrderReceipt failed:", e);
      toast.error("Print failed: " + (e?.message || "unknown"));
    }
  }, [paperSize, restaurant]);

  // Keep the ref pointing at the latest printOrderReceipt so the WS handler
  // can call it without depending on it directly.
  useEffect(() => {
    printOrderReceiptRef.current = printOrderReceipt;
  }, [printOrderReceipt]);

  const printReceipt = () => {
    if (!selectedOrder) { toast.error("No receipt to print"); return; }
    printOrderReceipt(selectedOrder);
  };

  // Kept for potential edge-cases — currently unused since printReceipt() routes through printOrderReceipt.
  // eslint-disable-next-line no-unused-vars
  const printReceiptLegacy = () => {
    if (!selectedOrder || !receiptRef.current) {
      toast.error("No receipt to print");
      return;
    }
    try {
      const receiptHtml = receiptRef.current.innerHTML;
      const isNarrow = paperSize === "58";

      // Remove any old print iframe
      const existing = document.getElementById("dalabsom-print-iframe");
      if (existing) existing.remove();

      const iframe = document.createElement("iframe");
      iframe.id = "dalabsom-print-iframe";
      iframe.style.position = "fixed";
      iframe.style.right = "0";
      iframe.style.bottom = "0";
      iframe.style.width = "0";
      iframe.style.height = "0";
      iframe.style.border = "0";
      iframe.style.opacity = "0";
      document.body.appendChild(iframe);

      const doc = iframe.contentWindow.document;
      doc.open();
      doc.write(`<!DOCTYPE html><html><head><title>Receipt #${selectedOrder.order_number}</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { font-family: 'Courier New', ui-monospace, monospace; font-size: ${isNarrow ? 10 : 11}px; line-height: 1.4; padding: ${isNarrow ? 4 : 8}px; width: ${isNarrow ? "58mm" : "80mm"}; max-width: ${isNarrow ? "58mm" : "80mm"}; color: #000; }
          .text-center { text-align: center; }
          .font-bold, h2 { font-weight: bold; }
          .text-base { font-size: ${isNarrow ? 12 : 14}px; }
          .text-xs { font-size: ${isNarrow ? 9 : 10}px; }
          .text-sm { font-size: ${isNarrow ? 10 : 12}px; }
          .font-mono { font-family: 'Courier New', monospace; }
          .font-medium { font-weight: 600; }
          .mb-1 { margin-bottom: 4px; } .mb-2 { margin-bottom: 8px; } .mb-3 { margin-bottom: 12px; }
          .pb-2 { padding-bottom: 8px; } .pt-1 { padding-top: 4px; }
          .border-b { border-bottom: 1px dashed #999; }
          .border-dashed { border-style: dashed; }
          .border-gray-300 { border-color: #ccc; }
          .space-y-0\\.5 > * + * { margin-top: 2px; }
          .space-y-1 > * + * { margin-top: 4px; }
          .flex { display: flex; } .justify-between { justify-content: space-between; }
          .text-green-600 { color: #16a34a; } .text-red-600 { color: #dc2626; }
          .text-gray-400, .text-gray-500 { color: #999; }
          .pl-3 { padding-left: 12px; }
          .uppercase { text-transform: uppercase; }
          .no-print { display: none !important; }
          @page { size: ${isNarrow ? "58mm" : "80mm"} auto; margin: 0; }
        </style></head><body>${receiptHtml}</body></html>`);
      doc.close();

      const doPrint = () => {
        try {
          iframe.contentWindow.focus();
          iframe.contentWindow.print();
        } catch (err) {
          console.error("Print error:", err);
          toast.error("Print failed — check printer connection");
        }
        // Cleanup after print dialog closes (Chrome fires onafterprint, Safari does not)
        const cleanup = () => { setTimeout(() => iframe.remove(), 500); };
        if (iframe.contentWindow.matchMedia) {
          const mql = iframe.contentWindow.matchMedia("print");
          mql.addEventListener?.("change", (e) => { if (!e.matches) cleanup(); });
        }
        // Safety net
        setTimeout(cleanup, 60000);
      };

      // Wait for images/fonts to load before printing
      if (iframe.contentDocument.readyState === "complete") {
        setTimeout(doPrint, 200);
      } else {
        iframe.onload = () => setTimeout(doPrint, 200);
      }
    } catch (e) {
      console.error("Print receipt failed:", e);
      toast.error("Could not open receipt for printing");
    }
  };

  const openEditDialog = (order) => {
    setSelectedOrder(order);
    setEditItems(order.items.map(i => ({
      menu_item_id: i.menu_item_id,
      name: i.name,
      quantity: i.quantity,
      unit_price: i.unit_price,
      variant_id: null,
      selected_extras: i.extras?.map(e => e.id) || [],
      notes: i.notes
    })));
    setEditDialog(true);
  };

  const handleLogout = () => { logout(); navigate("/login"); };

  const filteredOrders = orders.filter(order => {
    const matchesFilter = filter === "all" || 
      (filter === "unpaid" && order.payment_status === "unpaid") ||
      (filter === "paid" && order.payment_status === "paid") ||
      (filter === "pending" && order.payment_status === "pending") ||
      (filter === "active" && ["new", "preparing", "ready"].includes(order.status));
    const matchesSearch = searchQuery === "" || 
      order.order_number.toLowerCase().includes(searchQuery.toLowerCase()) ||
      order.table_number?.includes(searchQuery);
    return matchesFilter && matchesSearch;
  });

  const todayTotal = orders
    .filter(o => o.created_at?.startsWith(new Date().toISOString().split("T")[0]) && o.payment_status === "paid")
    .reduce((sum, o) => sum + o.total, 0);
  const unpaidCount = orders.filter(o => o.payment_status === "unpaid").length;
  const activeCount = orders.filter(o => ["new", "preparing", "ready"].includes(o.status)).length;

  const paymentMethodLabel = (m) => {
    const labels = { cash: "Cash", evc_plus: "EVC Plus", zaad: "Zaad", edahab: "E-Dahab", pay_at_cashier: "Pay at Cashier", pay_with_receipt: "Pay Later" };
    return labels[m] || m;
  };

  // Get merchant accounts from restaurant's manual_providers (active only)
  const merchantAccounts = (restaurant?.manual_providers || []).filter(p => p.active !== false);

  return (
    <div className="min-h-screen bg-gray-100" data-testid="cashier-dashboard">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 px-4 lg:px-6 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {restaurant?.logo_data ? (
              <img src={restaurant.logo_data} alt={restaurant.name} className="w-10 h-10 rounded-xl object-cover" />
            ) : (
              <div className="w-10 h-10 bg-primary rounded-xl flex items-center justify-center">
                <CreditCard className="w-5 h-5 text-white" />
              </div>
            )}
            <div>
              <h1 className="text-lg font-bold text-foreground">{restaurant?.name || "Cashier"}</h1>
              <p className="text-xs text-muted-foreground">Point of Sale</p>
            </div>
          </div>

          <div className="hidden md:flex items-center gap-4">
            <div className="text-center px-3 py-1 bg-green-50 rounded-lg">
              <p className="text-lg font-bold text-green-600">{formatCurrency(todayTotal)}</p>
              <p className="text-xs text-muted-foreground">Today</p>
            </div>
            <div className="text-center px-3 py-1 bg-red-50 rounded-lg">
              <p className="text-lg font-bold text-red-600">{unpaidCount}</p>
              <p className="text-xs text-muted-foreground">Unpaid</p>
            </div>
            <div className="text-center px-3 py-1 bg-blue-50 rounded-lg">
              <p className="text-lg font-bold text-blue-600">{activeCount}</p>
              <p className="text-xs text-muted-foreground">Active</p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Button onClick={() => navigate("/cashier/orders")} className="bg-sky-500 hover:bg-sky-600" size="sm" data-testid="take-order-btn">
              <Plus className="w-4 h-4 mr-1" /> Take Order
            </Button>
            <Button variant="ghost" size="icon" onClick={fetchOrders}><RefreshCw className="w-4 h-4" /></Button>
            <div className="hidden sm:flex items-center gap-1 text-muted-foreground">
              <User className="w-4 h-4" />
              <span className="text-xs">{user?.name}</span>
            </div>
            <Button variant="ghost" size="icon" onClick={handleLogout} data-testid="logout-btn"><LogOut className="w-4 h-4" /></Button>
          </div>
        </div>
      </header>

      <main className="p-4 lg:p-6">
        {/* Assistance Requests Banner */}
        {assistanceRequests.length > 0 && (
          <div className="mb-4 bg-amber-50 border-2 border-amber-400 rounded-xl p-3" data-testid="assistance-banner">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <Bell className="w-5 h-5 text-amber-600 animate-pulse" />
                <span className="font-bold text-amber-800 text-sm">
                  {assistanceRequests.length} table{assistanceRequests.length > 1 ? "s" : ""} need assistance
                </span>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              {assistanceRequests.map(r => (
                <div key={r.id} className="bg-white border border-amber-300 rounded-lg px-3 py-1.5 flex items-center gap-2 text-sm" data-testid={`assistance-${r.id}`}>
                  <span className="font-bold text-amber-900">T{r.table_number || r.qr_name || "?"}</span>
                  {r.reason && <span className="text-amber-700 text-xs">· {r.reason}</span>}
                  <Button size="sm" variant="ghost" className="h-6 px-2 text-xs text-emerald-700 hover:bg-emerald-50"
                    onClick={() => resolveAssistance(r.id)} data-testid={`resolve-assistance-${r.id}`}>
                    <CheckCircle className="w-3 h-3 mr-1" /> Resolve
                  </Button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-3 mb-4">
          <div className="relative flex-1 max-w-md">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input placeholder="Search order # or table..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="pl-9 h-10 bg-white" data-testid="order-search-input" />
          </div>
          <Tabs value={filter} onValueChange={setFilter} className="w-full sm:w-auto">
            <TabsList className="grid grid-cols-5 h-10">
              <TabsTrigger value="all" className="text-xs px-3">All</TabsTrigger>
              <TabsTrigger value="active" className="text-xs px-3">Active</TabsTrigger>
              <TabsTrigger value="unpaid" className="text-xs px-3">Unpaid</TabsTrigger>
              <TabsTrigger value="pending" className="text-xs px-3">Pending</TabsTrigger>
              <TabsTrigger value="paid" className="text-xs px-3">Paid</TabsTrigger>
            </TabsList>
          </Tabs>
        </div>

        {/* Orders Grid */}
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        ) : filteredOrders.length === 0 ? (
          <Card className="border-dashed bg-white">
            <CardContent className="flex flex-col items-center justify-center py-12">
              <Receipt className="w-12 h-12 text-muted-foreground/50 mb-4" />
              <h3 className="font-semibold text-lg mb-1">No orders found</h3>
              <p className="text-muted-foreground text-sm">Orders will appear here when placed</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
            {filteredOrders.map(order => {
              const statusInfo = ORDER_STATUSES[order.status];
              const paymentInfo = PAYMENT_STATUSES[order.payment_status];
              return (
                <Card 
                  key={order.id} 
                  className={cn(
                    "bg-white hover:shadow-md transition-all cursor-pointer border-l-4",
                    order.payment_status === "unpaid" && "border-l-red-500",
                    order.payment_status === "pending" && "border-l-yellow-500",
                    order.payment_status === "paid" && "border-l-green-500",
                    order.payment_status === "refunded" && "border-l-gray-400"
                  )}
                  onClick={() => { setSelectedOrder(order); setReceiptDialog(true); }}
                  data-testid={`order-row-${order.id}`}
                >
                  <CardContent className="p-3">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <span className="text-xl font-bold font-mono">#{order.order_number.split("-")[1]}</span>
                        <Badge className={cn("text-xs", order.order_type === "eat_in" ? "bg-blue-100 text-blue-700" : "bg-orange-100 text-orange-700")}>
                          {order.order_type === "eat_in" ? `T${order.table_number || "-"}` : "TW"}
                        </Badge>
                      </div>
                      <span className="text-lg font-bold text-primary">{formatCurrency(order.total)}</span>
                    </div>
                    
                    <div className="text-xs text-muted-foreground mb-2 line-clamp-2">
                      {order.items.map((item, idx) => (
                        <span key={idx} className={cn(
                          item.item_status === "served" && "line-through opacity-50",
                          item.item_status === "ready" && "text-green-600 font-medium",
                          item.item_status === "preparing" && "text-amber-600"
                        )}>
                          {item.quantity}x {item.name}{idx < order.items.length - 1 && ", "}
                        </span>
                      ))}
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-1">
                        <Badge className={cn(statusInfo?.color, "text-xs py-0")}>{statusInfo?.label}</Badge>
                        <Badge className={cn(paymentInfo?.color, "text-xs py-0")}>{paymentInfo?.label}</Badge>
                      </div>
                      <span className="text-xs text-muted-foreground">{formatTime(order.created_at)}</span>
                    </div>
                    
                    {order.payment_status === "unpaid" && (
                      <Button
                        className="w-full mt-2 bg-green-600 hover:bg-green-700 h-8 text-xs"
                        onClick={(e) => { e.stopPropagation(); setSelectedOrder(order); setPaymentDialog(true); }}
                        data-testid={`process-payment-${order.id}`}
                      >
                        <DollarSign className="w-3 h-3 mr-1" /> Process Payment
                      </Button>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </main>

      {/* ===== PAYMENT DIALOG ===== */}
      <Dialog open={paymentDialog} onOpenChange={setPaymentDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Process Payment — #{selectedOrder?.order_number?.split("-")[1]}</DialogTitle>
          </DialogHeader>
          {selectedOrder && (
            <div className="space-y-4">
              <div className="p-3 bg-muted rounded-lg flex justify-between items-center">
                <span className="text-sm text-muted-foreground">Total</span>
                <span className="text-2xl font-bold text-primary">{formatCurrency(selectedOrder.total)}</span>
              </div>
              <div className="p-3 bg-gray-50 rounded-lg flex justify-between items-center">
                <span className="text-sm">Method</span>
                <div className="flex items-center gap-2">
                  <Badge>{paymentMethodLabel(selectedOrder.payment_method)}</Badge>
                  <Button size="sm" variant="ghost" className="text-xs h-6" onClick={() => { setPaymentDialog(false); setNewPaymentMethod(selectedOrder.payment_method); setChangeMethodDialog(true); }}>
                    Change
                  </Button>
                </div>
              </div>

              {["evc_plus", "zaad", "edahab"].includes(selectedOrder.payment_method) ? (
                <div className="space-y-3">
                  <div className={cn("p-4 rounded-lg border",
                    selectedOrder.payment_method === "evc_plus" && "bg-orange-50 border-orange-200",
                    selectedOrder.payment_method === "zaad" && "bg-green-50 border-green-200",
                    selectedOrder.payment_method === "edahab" && "bg-blue-50 border-blue-200"
                  )}>
                    <p className="text-base font-bold mb-2">{paymentMethodLabel(selectedOrder.payment_method)}</p>
                    {merchantAccounts.map((p, i) => (
                      <div key={i} className="flex justify-between items-center py-1">
                        <span className="text-sm font-medium text-gray-700">{p.name}</span>
                        <span className="text-lg font-bold font-mono tracking-wide text-gray-900">{p.merchant_number}</span>
                      </div>
                    ))}
                  </div>
                  <Button className="w-full h-11 bg-green-600 hover:bg-green-700" onClick={confirmPayment} disabled={processing} data-testid="confirm-payment-btn">
                    <CheckCircle className="w-4 h-4 mr-2" /> {processing ? "Processing..." : "Confirm Payment Received"}
                  </Button>
                </div>
              ) : (
                <Button className="w-full h-11 bg-sky-500 hover:bg-sky-600" onClick={markPaidAtCounter} disabled={processing} data-testid="mark-paid-btn">
                  <CheckCircle className="w-4 h-4 mr-2" /> {processing ? "Processing..." : "Mark as Paid"}
                </Button>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* ===== CHANGE PAYMENT METHOD DIALOG ===== */}
      <Dialog open={changeMethodDialog} onOpenChange={setChangeMethodDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Change Payment Method</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Select value={newPaymentMethod} onValueChange={setNewPaymentMethod}>
              <SelectTrigger data-testid="change-method-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="cash">Cash</SelectItem>
                <SelectItem value="evc_plus">EVC Plus</SelectItem>
                <SelectItem value="zaad">Zaad</SelectItem>
                <SelectItem value="edahab">E-Dahab</SelectItem>
                <SelectItem value="pay_at_cashier">Pay at Cashier</SelectItem>
                <SelectItem value="pay_with_receipt">Pay Later</SelectItem>
              </SelectContent>
            </Select>
            <Button className="w-full" onClick={changePaymentMethod} disabled={processing}>
              {processing ? "Updating..." : "Update Method"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* ===== RECEIPT DIALOG ===== */}
      <Dialog open={receiptDialog} onOpenChange={setReceiptDialog}>
        <DialogContent className="max-w-sm max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="no-print">Order Details</DialogTitle></DialogHeader>
          {selectedOrder && (
            <>
              {/* Somalia hybrid payment verification panel — only shown for pending mobile-money orders */}
              {selectedOrder.payment_status === "pending" && selectedOrder.payment_unique_amount && (
                <div className="bg-amber-50 border border-amber-300 rounded-lg p-3 mb-3" data-testid="pending-payment-panel">
                  <p className="text-xs font-semibold text-amber-800 mb-1">⚠ Pending Mobile Money Payment</p>
                  <p className="text-xs text-amber-700 mb-2">Match this exact amount in your {paymentMethodLabel(selectedOrder.payment_method)} SMS:</p>
                  <div className="bg-white border border-amber-200 rounded p-2 mb-2">
                    <p className="text-2xl font-bold font-mono text-amber-900 text-center">
                      {formatCurrency(selectedOrder.payment_unique_amount)}
                    </p>
                  </div>
                  {selectedOrder.payment_proof ? (
                    <div className="bg-white rounded p-2 mb-2 text-xs space-y-0.5">
                      <p className="font-semibold text-slate-700">Customer submitted:</p>
                      <div className="flex justify-between"><span>Payer phone:</span><span className="font-mono">{selectedOrder.payment_proof.payer_number}</span></div>
                      {selectedOrder.payment_proof.transaction_ref && <div className="flex justify-between"><span>Reference:</span><span className="font-mono">{selectedOrder.payment_proof.transaction_ref}</span></div>}
                      {selectedOrder.payment_proof.paid_amount && <div className="flex justify-between"><span>Paid amount:</span><span className="font-mono">{formatCurrency(selectedOrder.payment_proof.paid_amount)}</span></div>}
                    </div>
                  ) : (
                    <p className="text-xs text-slate-500 italic mb-2">No customer proof submitted yet.</p>
                  )}
                  <div className="grid grid-cols-2 gap-2">
                    <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" disabled={processing}
                      onClick={() => verifySomaliaPayment("confirmed", "")} data-testid="verify-confirm-btn">
                      <CheckCircle className="w-4 h-4 mr-1" /> Confirm
                    </Button>
                    <Button size="sm" variant="outline" className="border-red-300 text-red-600 hover:bg-red-50" disabled={processing}
                      onClick={() => { const reason = window.prompt("Rejection reason?"); if (reason !== null) verifySomaliaPayment("rejected", reason); }}
                      data-testid="verify-reject-btn">
                      <X className="w-4 h-4 mr-1" /> Reject
                    </Button>
                  </div>
                </div>
              )}

              <div ref={receiptRef} id="receipt-content" className="receipt-thermal">
                <div className="text-center mb-3 pb-2 border-b border-dashed border-gray-300">
                  <h2 className="font-bold text-base">{restaurant?.name || "Dalabsom"}</h2>
                  {restaurant?.address && <p className="text-xs text-gray-500">{restaurant.address}, {restaurant.city}</p>}
                  {restaurant?.contact_phone && <p className="text-xs text-gray-500">Tel: {restaurant.contact_phone}</p>}
                </div>
                
                <div className="mb-2 pb-2 border-b border-dashed border-gray-300 text-xs space-y-0.5">
                  <div className="flex justify-between"><span>Order #</span><span className="font-mono font-bold">{selectedOrder.order_number}</span></div>
                  <div className="flex justify-between"><span>Date</span><span>{formatDate(selectedOrder.created_at)}</span></div>
                  <div className="flex justify-between"><span>Type</span><span>{selectedOrder.order_type === "eat_in" ? `Table ${selectedOrder.table_number || "-"}` : "Takeaway"}</span></div>
                  {user?.name && <div className="flex justify-between"><span>Cashier</span><span>{user.name}</span></div>}
                </div>
                
                <div className="mb-2 pb-2 border-b border-dashed border-gray-300 space-y-1">
                  {selectedOrder.items.map((item, idx) => (
                    <div key={idx}>
                      <div className="flex justify-between text-xs">
                        <span>{item.quantity}x {item.name}</span>
                        <span>{formatCurrency(item.total)}</span>
                      </div>
                      {item.extras?.length > 0 && (
                        <div className="text-xs text-gray-500 pl-3">
                          {item.extras.map(e => `+${e.name}`).join(", ")}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
                
                <div className="mb-2 pb-2 border-b border-dashed border-gray-300 space-y-0.5 text-xs">
                  <div className="flex justify-between"><span>Subtotal</span><span>{formatCurrency(selectedOrder.subtotal || selectedOrder.total)}</span></div>
                  {selectedOrder.vat_amount > 0 && (
                    <div className="flex justify-between"><span>VAT ({selectedOrder.vat_percentage}%)</span><span>{formatCurrency(selectedOrder.vat_amount)}</span></div>
                  )}
                  <div className="flex justify-between font-bold text-sm pt-1">
                    <span>TOTAL</span><span>{formatCurrency(selectedOrder.total)}</span>
                  </div>
                </div>

                <div className="mb-2 pb-2 border-b border-dashed border-gray-300 text-xs space-y-0.5">
                  <div className="flex justify-between"><span>Payment</span><span className="uppercase font-medium">{paymentMethodLabel(selectedOrder.payment_method)}</span></div>
                  <div className="flex justify-between"><span>Status</span>
                    <span className={selectedOrder.payment_status === "paid" ? "text-green-600 font-bold" : "text-red-600 font-bold"}>
                      {selectedOrder.payment_status.toUpperCase()}
                    </span>
                  </div>
                  {selectedOrder.transaction_id && <div className="flex justify-between"><span>Ref</span><span className="font-mono">{selectedOrder.transaction_id}</span></div>}
                </div>

                {/* Merchant Account Numbers on Receipt — LARGER than other sections */}
                {merchantAccounts.length > 0 && (
                  <div className="mb-3 pb-2 border-b-2 border-dashed border-gray-400" style={{borderTopWidth: "2px", borderTopStyle: "dashed", borderTopColor: "#666", paddingTop: "6px", marginTop: "4px"}}>
                    <p className="font-bold text-center mb-1" style={{fontSize: "18px", letterSpacing: "1px"}}>PAYMENT OPTIONS</p>
                    {selectedOrder.payment_status !== "paid" && (
                      <p className="text-center mb-2" style={{fontSize: "12px"}}>
                        Total to pay: <span className="font-bold">{formatCurrency(selectedOrder.payment_unique_amount || selectedOrder.total)}</span>
                      </p>
                    )}
                    {merchantAccounts.map((p, i) => {
                      // Use literal "$" placeholder — customer types real amount when dialing.
                      let ussd = null;
                      if (p.ussd_code && p.ussd_code.includes("*")) {
                        ussd = p.ussd_code
                          .replace(/\{merchant\}/gi, p.merchant_number)
                          .replace(/\{amount\}/gi, "$")
                          .replace(/\{total\}/gi, "$");
                      }
                      return (
                        <div key={i} className="mb-2" style={{fontSize: "14px"}}>
                          <div className="flex justify-between">
                            <span className="font-bold">{p.name}</span>
                            <span className="font-mono font-bold" style={{fontSize: "15px", letterSpacing: "0.5px"}}>{p.merchant_number}</span>
                          </div>
                          {ussd && (
                            <div className="font-mono text-center mt-1 px-2 py-1 bg-black text-white rounded font-bold" style={{fontSize: "16px", letterSpacing: "1px"}}>
                              {ussd}
                            </div>
                          )}
                          {p.instructions && (
                            <p className="text-center italic text-gray-600 mt-0.5" style={{fontSize: "10px"}}>{p.instructions}</p>
                          )}
                        </div>
                      );
                    })}
                    {selectedOrder.payment_status !== "paid" && (
                      <p className="text-center text-gray-600 mt-1" style={{fontSize: "10px"}}>Replace <span className="font-mono font-bold">$</span> with the amount when dialing.</p>
                    )}
                  </div>
                )}

                {/* Split payments */}
                {selectedOrder.split_bill && selectedOrder.split_payments?.length > 0 && (
                  <div className="mb-2 pb-2 border-b border-dashed border-gray-300 text-xs">
                    <p className="font-bold mb-1">Split Payments:</p>
                    {selectedOrder.split_payments.map((sp, i) => (
                      <div key={i} className="flex justify-between">
                        <span>{sp.payer_name || `Split ${i+1}`} ({paymentMethodLabel(sp.payment_method)})</span>
                        <span className={cn("font-medium", sp.status === "paid" ? "text-green-600" : "text-red-600")}>
                          {formatCurrency(sp.amount)} {sp.status === "paid" ? "PAID" : "DUE"}
                        </span>
                      </div>
                    ))}
                  </div>
                )}

                <div className="text-center text-xs text-gray-400 mt-2">
                  <p>Thank you for dining with us!</p>
                  <p>Powered by Dalabsom</p>
                </div>
              </div>
              
              {/* Action Buttons (not printed) */}
              <div className="no-print grid grid-cols-2 gap-2 mt-3">
                {selectedOrder.payment_status === "unpaid" && (
                  <Button variant="outline" onClick={() => { setReceiptDialog(false); setPaymentDialog(true); }} data-testid="receipt-process-payment">
                    <DollarSign className="w-3 h-3 mr-1" /> Pay
                  </Button>
                )}
                {selectedOrder.status !== "completed" && selectedOrder.status !== "cancelled" && (
                  <Button variant="outline" onClick={() => { setReceiptDialog(false); openEditDialog(selectedOrder); }} data-testid="edit-order-btn">
                    <Edit className="w-3 h-3 mr-1" /> Edit
                  </Button>
                )}
                <Button variant="outline" onClick={() => { setReceiptDialog(false); setSplitDialog(true); }} data-testid="split-bill-btn">
                  <Split className="w-3 h-3 mr-1" /> Split
                </Button>
                {selectedOrder.payment_status === "paid" && (user?.role === "owner" || user?.role === "manager") && (
                  <Button variant="outline" className="border-red-200 text-red-600 hover:bg-red-50" onClick={() => { setReceiptDialog(false); setRefundDialog(true); }} data-testid="refund-btn">
                    <RotateCcw className="w-3 h-3 mr-1" /> Refund
                  </Button>
                )}
                <Button onClick={printReceipt} className="col-span-2" data-testid="print-receipt-btn">
                  <Printer className="w-3 h-3 mr-1" /> Print Receipt ({paperSize}mm)
                </Button>
                <div className="col-span-2 flex items-center justify-center gap-2 text-xs text-muted-foreground">
                  <span>Paper:</span>
                  <button onClick={() => { setPaperSize("58"); localStorage.setItem("dalabsom_paper_size", "58"); }}
                    className={cn("px-2 py-0.5 rounded border", paperSize === "58" ? "bg-primary text-white border-primary" : "border-slate-300")}
                    data-testid="paper-58">58mm</button>
                  <button onClick={() => { setPaperSize("80"); localStorage.setItem("dalabsom_paper_size", "80"); }}
                    className={cn("px-2 py-0.5 rounded border", paperSize === "80" ? "bg-primary text-white border-primary" : "border-slate-300")}
                    data-testid="paper-80">80mm</button>
                  <span className="ml-2">·</span>
                  <button
                    onClick={() => {
                      const next = !autoPrint;
                      setAutoPrint(next);
                      localStorage.setItem("dalabsom_auto_print", next ? "1" : "0");
                      toast.success(next ? "Auto-print enabled" : "Auto-print disabled");
                    }}
                    className={cn("px-2 py-0.5 rounded border", autoPrint ? "bg-emerald-600 text-white border-emerald-600" : "border-slate-300")}
                    data-testid="auto-print-toggle"
                  >
                    Auto-print {autoPrint ? "ON" : "OFF"}
                  </button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* ===== EDIT ORDER DIALOG ===== */}
      <Dialog open={editDialog} onOpenChange={setEditDialog}>
        <DialogContent className="max-w-md max-h-[80vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Edit Order #{selectedOrder?.order_number?.split("-")[1]}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            {editItems.map((item, idx) => (
              <div key={idx} className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg">
                <div className="flex-1">
                  <p className="text-sm font-medium">{item.name}</p>
                  <p className="text-xs text-muted-foreground">{formatCurrency(item.unit_price)} each</p>
                </div>
                <div className="flex items-center gap-1">
                  <Button size="icon" variant="outline" className="h-7 w-7" onClick={() => {
                    const updated = [...editItems];
                    if (updated[idx].quantity <= 1) { updated.splice(idx, 1); } else { updated[idx].quantity--; }
                    setEditItems(updated);
                  }}><Minus className="w-3 h-3" /></Button>
                  <span className="w-6 text-center text-sm font-medium">{item.quantity}</span>
                  <Button size="icon" variant="outline" className="h-7 w-7" onClick={() => {
                    const updated = [...editItems];
                    updated[idx].quantity++;
                    setEditItems(updated);
                  }}><Plus className="w-3 h-3" /></Button>
                  <Button size="icon" variant="ghost" className="h-7 w-7 text-red-500" onClick={() => {
                    setEditItems(editItems.filter((_, i) => i !== idx));
                  }}><X className="w-3 h-3" /></Button>
                </div>
              </div>
            ))}
            
            {/* Add item from menu */}
            <div className="border-t pt-3">
              <p className="text-sm font-medium mb-2">Add item:</p>
              <div className="grid grid-cols-2 gap-1 max-h-40 overflow-y-auto">
                {menuItems.filter(i => i.is_available).map(item => (
                  <Button key={item.id} size="sm" variant="outline" className="text-xs h-auto py-1 justify-start" onClick={() => {
                    setEditItems([...editItems, { menu_item_id: item.id, name: item.name, quantity: 1, unit_price: item.price, variant_id: null, selected_extras: [], notes: "" }]);
                  }}>
                    <Plus className="w-3 h-3 mr-1 flex-shrink-0" /> {item.name}
                  </Button>
                ))}
              </div>
            </div>

            <div className="flex justify-between items-center pt-2 border-t">
              <span className="font-medium">New Total:</span>
              <span className="text-lg font-bold text-primary">
                {formatCurrency(editItems.reduce((sum, i) => sum + i.unit_price * i.quantity, 0))}
              </span>
            </div>
            <Button className="w-full" onClick={updateOrderItems} disabled={processing} data-testid="save-edit-btn">
              {processing ? "Saving..." : "Save Changes"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* ===== SPLIT BILL DIALOG ===== */}
      <Dialog open={splitDialog} onOpenChange={setSplitDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Split Bill — #{selectedOrder?.order_number?.split("-")[1]}</DialogTitle></DialogHeader>
          {selectedOrder && (
            <div className="space-y-4">
              <div className="p-3 bg-muted rounded-lg space-y-1">
                <div className="flex justify-between text-sm"><span>Order Total</span><span className="font-bold">{formatCurrency(selectedOrder.total)}</span></div>
                <div className="flex justify-between text-sm"><span>Amount Paid</span><span className="text-green-600 font-bold">{formatCurrency(selectedOrder.amount_paid || 0)}</span></div>
                <div className="flex justify-between text-sm"><span>Remaining</span><span className="text-red-600 font-bold">{formatCurrency(selectedOrder.amount_remaining || selectedOrder.total)}</span></div>
              </div>

              {/* Existing splits */}
              {selectedOrder.split_payments?.length > 0 && (
                <div className="space-y-2">
                  <p className="text-sm font-medium">Split Payments:</p>
                  {selectedOrder.split_payments.map((sp, i) => (
                    <div key={i} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg text-sm">
                      <div>
                        <span className="font-medium">{sp.payer_name || `Person ${i+1}`}</span>
                        <span className="text-muted-foreground ml-2">{paymentMethodLabel(sp.payment_method)}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="font-bold">{formatCurrency(sp.amount)}</span>
                        {sp.status === "paid" ? (
                          <Badge className="bg-green-100 text-green-700 text-xs">Paid</Badge>
                        ) : (
                          <Button size="sm" className="h-6 text-xs bg-green-600 hover:bg-green-700" onClick={() => processSplitPayment(sp.id)} disabled={processing}>
                            Confirm
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Add new split */}
              <div className="border-t pt-3 space-y-3">
                <p className="text-sm font-medium">Add Split Payment:</p>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-xs">Amount</Label>
                    <Input type="number" value={splitAmount} onChange={(e) => setSplitAmount(e.target.value)} placeholder="0.00" className="h-9" data-testid="split-amount-input" />
                  </div>
                  <div>
                    <Label className="text-xs">Method</Label>
                    <Select value={splitMethod} onValueChange={setSplitMethod}>
                      <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="cash">Cash</SelectItem>
                        <SelectItem value="evc_plus">EVC Plus</SelectItem>
                        <SelectItem value="zaad">Zaad</SelectItem>
                        <SelectItem value="edahab">E-Dahab</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <Input value={splitPayerName} onChange={(e) => setSplitPayerName(e.target.value)} placeholder="Payer name (optional)" className="h-9" />
                <Button className="w-full" onClick={addSplitPayment} disabled={processing} data-testid="add-split-btn">
                  <Plus className="w-4 h-4 mr-2" /> {processing ? "Adding..." : "Add Split Payment"}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* ===== REFUND DIALOG ===== */}
      <Dialog open={refundDialog} onOpenChange={setRefundDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Process Refund — #{selectedOrder?.order_number}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="p-3 bg-red-50 rounded-lg border border-red-200 flex justify-between items-center">
              <span className="text-red-700">Refund Amount</span>
              <span className="text-2xl font-bold text-red-700">{formatCurrency(selectedOrder?.total || 0)}</span>
            </div>
            <div className="space-y-2">
              <Label>Reason for Refund *</Label>
              <Textarea value={refundReason} onChange={(e) => setRefundReason(e.target.value)} placeholder="e.g., Wrong order, customer complaint" className="h-20" data-testid="refund-reason-input" />
            </div>
            <div className="flex gap-2">
              <Button variant="outline" className="flex-1" onClick={() => { setRefundDialog(false); setRefundReason(""); }}>Cancel</Button>
              <Button className="flex-1 bg-red-600 hover:bg-red-700" onClick={processRefund} disabled={processing || !refundReason.trim()} data-testid="confirm-refund-btn">
                <RotateCcw className="w-4 h-4 mr-2" /> {processing ? "Processing..." : "Confirm Refund"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CashierDashboard;
